import React, { Component } from 'react';
import { Modal, Button, Form } from 'react-bootstrap';
import { UNIFIED_ROLES, isValidUnifiedRole } from '../../../util/staffRoles';

/**
 * AddStaffModal - Modal for adding or editing staff members
 *
 * Props:
 * - show: boolean - whether modal is visible
 * - onHide: function - called when modal should close
 * - onSave: function(staffData) - called with staff data to save
 * - onResendInvite: function(staff) - called to resend password email
 * - editingStaff: object|null - if editing, the staff object; if adding, null
 * - userType: 'installer' or 'dealer' - determines available roles
 */
class AddStaffModal extends Component {
  state = {
    name: '',
    email: '',
    phone: '',
    role: '',
    active: true,
    createLogin: true, // For dealers: whether to create Cognito account
    errors: {},
    saving: false,
    resending: false
  };

  componentDidUpdate(prevProps) {
    // When modal opens or editingStaff changes, populate form
    if (this.props.show && !prevProps.show) {
      this.populateForm();
    }
  }

  populateForm = () => {
    const { editingStaff, userType } = this.props;
    // NEW: Default to 'viewer' for unified roles
    const defaultRole = 'viewer';

    if (editingStaff) {
      // NEW: Use the new 'role' field first, fall back to user_role for legacy
      let role = editingStaff.role || editingStaff.user_role || '';

      // If not a unified role, try to find a mapping
      if (role && !isValidUnifiedRole(role)) {
        // For legacy dealer roles, map to unified
        const normalized = role.toLowerCase().trim();
        if (normalized === 'owner' || normalized === 'manager') {
          role = 'admin';
        } else if (normalized === 'finance') {
          role = 'manager';
        } else if (normalized === 'sales' || normalized === 'technician') {
          role = 'technician';
        } else {
          role = 'viewer';
        }
      }

      this.setState({
        name: editingStaff.user_name || '',
        email: editingStaff.Email || '',
        phone: editingStaff.user_phone_number || '',
        role: role || defaultRole,
        active: editingStaff.active === 1 || editingStaff.active === true,
        createLogin: true, // Not relevant for editing
        errors: {},
        saving: false,
        resending: false
      });
    } else {
      // Reset form for new staff
      this.setState({
        name: '',
        email: '',
        phone: '',
        role: defaultRole,
        active: true,
        createLogin: userType === 'dealer' ? true : true, // Installers always create login
        errors: {},
        saving: false,
        resending: false
      });
    }
  };

  handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === 'checkbox' ? checked : value;

    // Clear error for this field
    const errors = { ...this.state.errors };
    delete errors[name];

    this.setState({
      [name]: newValue,
      errors
    });
  };

  validate = () => {
    const { name, email, phone, role } = this.state;
    const { editingStaff } = this.props;
    const errors = {};

    // Name validation
    if (!name || name.trim().length < 2) {
      errors.name = 'Name is required (at least 2 characters)';
    }

    // Email validation (only required for new staff)
    if (!editingStaff) {
      if (!email) {
        errors.email = 'Email is required';
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        errors.email = 'Please enter a valid email address';
      }
    }

    // Phone validation (optional but if provided, should be valid)
    if (phone && !/^[0-9]{10}$/.test(phone.replace(/\D/g, ''))) {
      errors.phone = 'Phone should be 10 digits';
    }

    // NEW: Role validation using unified roles
    const validRoles = UNIFIED_ROLES.map(r => r.value);
    if (!role || !validRoles.includes(role)) {
      errors.role = 'Please select a valid role';
    }

    this.setState({ errors });
    return Object.keys(errors).length === 0;
  };

  handleSave = async () => {
    if (!this.validate()) return;

    this.setState({ saving: true });

    try {
      const { name, email, phone, role, active, createLogin } = this.state;
      const { userType, editingStaff } = this.props;

      await this.props.onSave({
        name: name.trim(),
        email: email.trim().toLowerCase(),
        phone: phone ? phone.replace(/\D/g, '') : '',
        role,
        active,
        // For new users: installers always create login, dealers have option
        createLogin: editingStaff ? undefined : (userType === 'installer' ? true : createLogin)
      });
    } catch (error) {
      this.setState({ saving: false });
      // Error handling is done in parent component
    }
  };

  handleClose = () => {
    if (this.state.saving || this.state.resending) return; // Don't close while saving or resending
    this.props.onHide();
  };

  handleResendInvite = async () => {
    const { onResendInvite, editingStaff } = this.props;
    if (!onResendInvite || !editingStaff) return;

    this.setState({ resending: true });
    try {
      await onResendInvite(editingStaff);
    } finally {
      this.setState({ resending: false });
    }
  };

  render() {
    const { show, editingStaff, userType, onResendInvite } = this.props;
    const { name, email, phone, role, active, createLogin, errors, saving, resending } = this.state;

    const isEditing = !!editingStaff;
    const isDealer = userType === 'dealer';
    // NEW: Use unified roles instead of user-type specific roles
    const roles = UNIFIED_ROLES;
    const selectedRoleInfo = roles.find(r => r.value === role);

    const title = isEditing
      ? `Edit ${isDealer ? 'Team' : 'Staff'} Member`
      : `Add New ${isDealer ? 'Team' : 'Staff'} Member`;

    return (
      <Modal show={show} onHide={this.handleClose} backdrop="static">
        <Modal.Header closeButton>
          <Modal.Title>{title}</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <Form>
            {/* Name */}
            <Form.Group className="mb-3">
              <Form.Label>Full Name *</Form.Label>
              <Form.Control
                type="text"
                name="name"
                value={name}
                onChange={this.handleChange}
                placeholder="Enter full name"
                isInvalid={!!errors.name}
                disabled={saving}
              />
              <Form.Control.Feedback type="invalid">
                {errors.name}
              </Form.Control.Feedback>
            </Form.Group>

            {/* Email - only editable for new staff */}
            <Form.Group className="mb-3">
              <Form.Label>Email {!isEditing && '*'}</Form.Label>
              <Form.Control
                type="email"
                name="email"
                value={email}
                onChange={this.handleChange}
                placeholder="Enter email address"
                isInvalid={!!errors.email}
                disabled={saving || isEditing}
              />
              {isEditing && (
                <Form.Text className="text-muted">
                  Email cannot be changed after account creation
                </Form.Text>
              )}
              <Form.Control.Feedback type="invalid">
                {errors.email}
              </Form.Control.Feedback>
            </Form.Group>

            {/* Phone */}
            <Form.Group className="mb-3">
              <Form.Label>Phone Number</Form.Label>
              <Form.Control
                type="tel"
                name="phone"
                value={phone}
                onChange={this.handleChange}
                placeholder="1234567890"
                isInvalid={!!errors.phone}
                disabled={saving}
              />
              <Form.Control.Feedback type="invalid">
                {errors.phone}
              </Form.Control.Feedback>
            </Form.Group>

            {/* Role */}
            <Form.Group className="mb-3">
              <Form.Label>Role *</Form.Label>
              <Form.Control
                as="select"
                name="role"
                value={role}
                onChange={this.handleChange}
                isInvalid={!!errors.role}
                disabled={saving}
              >
                {roles.map(r => (
                  <option key={r.value} value={r.value}>{r.label}</option>
                ))}
              </Form.Control>
              <Form.Control.Feedback type="invalid">
                {errors.role}
              </Form.Control.Feedback>
              {selectedRoleInfo && (
                <Form.Text className="text-muted">
                  {selectedRoleInfo.description}
                </Form.Text>
              )}
            </Form.Group>

            {/* Create Login - only show for dealers when adding new */}
            {isDealer && !isEditing && (
              <Form.Group className="mb-3">
                <Form.Check
                  type="checkbox"
                  name="createLogin"
                  label="Send login invitation"
                  checked={createLogin}
                  onChange={this.handleChange}
                  disabled={saving}
                />
                <Form.Text className="text-muted">
                  {createLogin
                    ? "They'll receive an email to set up their password and access the system"
                    : "They'll receive notifications but won't be able to log in (contact only)"}
                </Form.Text>
              </Form.Group>
            )}

            {/* Active Status - only show for editing */}
            {isEditing && (
              <Form.Group className="mb-3">
                <Form.Check
                  type="checkbox"
                  name="active"
                  label="Account Active"
                  checked={active}
                  onChange={this.handleChange}
                  disabled={saving}
                />
                <Form.Text className="text-muted">
                  Inactive accounts cannot log in or receive notifications
                </Form.Text>
              </Form.Group>
            )}

            {/* Password Reset - only show for editing when user has login access */}
            {isEditing && onResendInvite && (editingStaff.cognito_status === 'active' || !isDealer) && (
              <div className="border-top pt-3 mt-3">
                <div className="d-flex align-items-center justify-content-between">
                  <div>
                    <strong>Password Reset</strong>
                    <div className="text-muted small">Send a password reset email to this user</div>
                  </div>
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={this.handleResendInvite}
                    disabled={saving || resending}
                  >
                    {resending ? (
                      <>
                        <span className="spinner-border spinner-border-sm mr-1"></span>
                        Sending...
                      </>
                    ) : (
                      <>
                        <i className="fa-sharp fa-regular fa-envelope mr-1"></i>
                        Send Reset Email
                      </>
                    )}
                  </Button>
                </div>
              </div>
            )}
          </Form>
        </Modal.Body>

        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={this.handleClose}
            disabled={saving || resending}
          >
            CANCEL
          </Button>
          <Button
            variant="primary"
            onClick={this.handleSave}
            disabled={saving || resending}
          >
            {saving ? (
              <>
                <span className="spinner-border spinner-border-sm mr-2" role="status" aria-hidden="true"></span>
                SAVING...
              </>
            ) : (
              isEditing
                ? `UPDATE ${isDealer ? 'TEAM MEMBER' : 'STAFF'}`
                : `ADD ${isDealer ? 'TEAM MEMBER' : 'STAFF'}`
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    );
  }
}

export default AddStaffModal;
